import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import AnimatedSection from '@/components/base/AnimatedSection';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">分类不存在</h1>
          <Link to="/" className="text-primary-500 hover:underline">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name} - ${siteInfo.name}`}
        description={category.description}
        keywords={`${category.name},黑料吃瓜,爆料`}
        canonical={`/category/${category.slug}`}
        schema={[
          {
            '@type': 'WebPage',
            name: `${category.name} - ${siteInfo.name}`,
            url: `/category/${category.slug}`,
            description: category.description,
            inLanguage: 'zh-CN',
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: category.name, item: `/category/${category.slug}` },
            ],
          },
        ]}
      />
      <Navbar />
      <main className="w-full pt-16 md:pt-20">
        <AnimatedSection>
          <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={category.coverImage}
              alt={category.name}
              title={`${category.name} - 黑料吃瓜独家爆料分类`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-white mb-3 drop-shadow-lg">{category.name}</h1>
              <p className="text-sm md:text-base text-white/90 max-w-xl drop-shadow-md">{category.description}</p>
            </div>
          </section>
        </AnimatedSection>

        <div className="w-full px-4 md:px-8 lg:px-12">
          <Breadcrumbs items={[{ label: category.name }]} />
        </div>

        <AnimatedSection delay={100}>
          <section className="w-full px-4 md:px-8 lg:px-12 py-4 md:py-6">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200/70 text-foreground-700 hover:bg-background-300/70 hover:text-foreground-950'
                  }`}
                >
                  {cat.name}
                  <span className="ml-1.5 text-xs opacity-70">({cat.articleCount})</span>
                </Link>
              ))}
            </div>
          </section>
        </AnimatedSection>

        <AnimatedSection delay={200}>
          <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">全部文章</h2>
                <p className="text-sm text-foreground-600 mt-1">共 {categoryArticles.length} 篇爆料</p>
              </div>
            </div>
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </section>
        </AnimatedSection>
      </main>
      <Footer />
    </>
  );
}